import React, { useState, useMemo } from 'react';
import './Calendar.css';

function Calendar({ signatures, onDayClick, onSignatureClick, viewMode = 'single', selectedAdGroup = null, adGroups = [] }) {
    const [currentDate, setCurrentDate] = useState(new Date());

    // Générer une couleur unique pour chaque groupe AD
    const adGroupColors = useMemo(() => {
        const colors = [
            { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' }, // Bleu
            { bg: '#fce7f3', border: '#ec4899', text: '#9f1239' }, // Rose
            { bg: '#dcfce7', border: '#10b981', text: '#065f46' }, // Vert
            { bg: '#fed7aa', border: '#f97316', text: '#9a3412' }, // Orange
            { bg: '#e9d5ff', border: '#a855f7', text: '#6b21a8' }, // Violet
            { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' }, // Jaune
            { bg: '#cffafe', border: '#06b6d4', text: '#164e63' }, // Cyan
            { bg: '#fecaca', border: '#ef4444', text: '#991b1b' }, // Rouge
        ];

        const colorMap = {};
        adGroups.forEach((group, index) => {
            colorMap[group.id] = colors[index % colors.length];
        });

        return colorMap;
    }, [adGroups]);

    // Fonctions utilitaires de date
    const getMonthName = (date) => {
        return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        // Convertir pour que lundi = 0, dimanche = 6
        return firstDay === 0 ? 6 : firstDay - 1;
    };

    const previousMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const nextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    // Fonction helper : calculer la priorité de groupe maximale pour une signature
    const getMaxGroupPriority = (signature) => {
        // Si pas de groupe AD, priorité = 0
        if (!signature.ad_group_ids || signature.ad_group_ids.length === 0) {
            return 0;
        }

        // Récupérer les priorités de tous les groupes de la signature
        const priorities = signature.ad_group_ids.map(groupId => {
            const group = adGroups.find(g => g.id === groupId);
            return group?.group_priority ?? 0;
        });

        // Retourner la priorité maximale
        return Math.max(...priorities, 0);
    };

    // Obtenir LA signature qui sera utilisée pour un jour donné
    // selon la logique : Active + Dates valides + Priorité Groupe + Priorité Signature + Fallback défaut
    const getSignaturesForDay = (day) => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const date = new Date(year, month, day);
        date.setHours(12, 0, 0, 0); // Midi pour éviter les problèmes de timezone

        // 1. Filtrer les signatures actives
        const activeSignatures = signatures.filter(sig => sig.is_active);

        // 2. Filtrer celles qui sont valides pour ce jour
        const validSignatures = activeSignatures.filter(sig => {
            const validFrom = sig.valid_from ? new Date(sig.valid_from) : null;
            const validUntil = sig.valid_until ? new Date(sig.valid_until) : null;

            // Si pas de dates définies, ne pas inclure (fallback sera géré après)
            if (!validFrom && !validUntil) return false;

            // Vérifier si la date est dans la plage
            const from = validFrom ? new Date(validFrom) : null;
            const until = validUntil ? new Date(validUntil) : null;

            from?.setHours(0, 0, 0, 0);
            until?.setHours(23, 59, 59, 999);

            if (from && until) {
                return date >= from && date <= until;
            } else if (from) {
                return date >= from;
            } else if (until) {
                return date <= until;
            }
            return false;
        });

        // 3. Si des signatures sont valides pour ce jour, trier par priorité groupe puis signature
        if (validSignatures.length > 0) {
            validSignatures.sort((a, b) => {
                // a) Priorité de groupe AD (la plus haute)
                const groupPriorityA = getMaxGroupPriority(a);
                const groupPriorityB = getMaxGroupPriority(b);

                if (groupPriorityB !== groupPriorityA) {
                    return groupPriorityB - groupPriorityA; // Décroissant
                }

                // b) Priorité de signature
                const priorityA = a.priority !== null && a.priority !== undefined ? a.priority : 0;
                const priorityB = b.priority !== null && b.priority !== undefined ? b.priority : 0;

                if (priorityB !== priorityA) {
                    return priorityB - priorityA; // Décroissant
                }

                // c) En cas d'égalité, prendre la plus récente (updated_at)
                const dateA = new Date(a.updated_at || a.created_at);
                const dateB = new Date(b.updated_at || b.created_at);
                return dateB - dateA;
            });

            // Retourner uniquement LA signature sélectionnée
            return [validSignatures[0]];
        }

        // 4. Si aucune signature avec dates valides, utiliser la signature par défaut
        const defaultSignature = activeSignatures.find(sig => sig.is_default);
        return defaultSignature ? [defaultSignature] : [];
    };

    // Obtenir les signatures pour chaque groupe AD (mode multi-groupe)
    const getSignaturesForDayMultiGroup = (day) => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const date = new Date(year, month, day);
        date.setHours(12, 0, 0, 0);

        const result = [];

        // Pour chaque groupe AD (ou "sans groupe")
        const groupsToProcess = selectedAdGroup
            ? [adGroups.find(g => g.id === selectedAdGroup)]
            : [...adGroups, { id: null, name: 'Sans groupe' }];

        groupsToProcess.forEach(group => {
            if (!group) return;

            // 1. Filtrer signatures actives
            let activeSignatures = signatures.filter(sig => sig.is_active);

            // 2. Filtrer par groupe AD
            if (group.id === null) {
                // Signatures sans groupe assigné
                activeSignatures = activeSignatures.filter(sig =>
                    !sig.ad_group_ids || sig.ad_group_ids.length === 0
                );
            } else {
                // Signatures assignées à ce groupe
                activeSignatures = activeSignatures.filter(sig =>
                    sig.ad_group_ids && sig.ad_group_ids.includes(group.id)
                );
            }

            // 3. Filtrer par dates valides
            const validSignatures = activeSignatures.filter(sig => {
                const validFrom = sig.valid_from ? new Date(sig.valid_from) : null;
                const validUntil = sig.valid_until ? new Date(sig.valid_until) : null;

                if (!validFrom && !validUntil) return false;

                const from = validFrom ? new Date(validFrom) : null;
                const until = validUntil ? new Date(validUntil) : null;

                from?.setHours(0, 0, 0, 0);
                until?.setHours(23, 59, 59, 999);

                if (from && until) {
                    return date >= from && date <= until;
                } else if (from) {
                    return date >= from;
                } else if (until) {
                    return date <= until;
                }
                return false;
            });

            // 4. Trier par priorité groupe puis signature
            if (validSignatures.length > 0) {
                validSignatures.sort((a, b) => {
                    // a) Priorité de groupe AD (déjà filtrées pour ce groupe, donc toutes ont la même)
                    // Mais on applique quand même la logique pour les signatures multi-groupes
                    const groupPriorityA = getMaxGroupPriority(a);
                    const groupPriorityB = getMaxGroupPriority(b);

                    if (groupPriorityB !== groupPriorityA) {
                        return groupPriorityB - groupPriorityA;
                    }

                    // b) Priorité de signature
                    const priorityA = a.priority !== null && a.priority !== undefined ? a.priority : 0;
                    const priorityB = b.priority !== null && b.priority !== undefined ? b.priority : 0;

                    if (priorityB !== priorityA) {
                        return priorityB - priorityA;
                    }

                    // c) Plus récente
                    const dateA = new Date(a.updated_at || a.created_at);
                    const dateB = new Date(b.updated_at || b.created_at);
                    return dateB - dateA;
                });

                result.push({
                    signature: validSignatures[0],
                    adGroup: group
                });
            } else {
                // 5. Fallback signature par défaut pour ce groupe
                const defaultSignature = activeSignatures.find(sig => sig.is_default);
                if (defaultSignature) {
                    result.push({
                        signature: defaultSignature,
                        adGroup: group
                    });
                }
            }
        });

        return result;
    };

    // Vérifier si c'est aujourd'hui
    const isToday = (day) => {
        const today = new Date();
        return today.getDate() === day &&
               today.getMonth() === currentDate.getMonth() &&
               today.getFullYear() === currentDate.getFullYear();
    };

    // Générer les jours du calendrier
    const renderCalendarDays = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const firstDay = getFirstDayOfMonth(currentDate);
        const days = [];

        // Jours vides avant le premier jour du mois
        for (let i = 0; i < firstDay; i++) {
            days.push(
                <div key={`empty-${i}`} className="calendar-day calendar-day-empty"></div>
            );
        }

        // Jours du mois
        for (let day = 1; day <= daysInMonth; day++) {
            const today = isToday(day);

            if (viewMode === 'multi-group') {
                // Mode multi-groupe : afficher une signature par groupe AD
                const daySignaturesMulti = getSignaturesForDayMultiGroup(day);

                days.push(
                    <div
                        key={day}
                        className={`calendar-day ${today ? 'calendar-day-today' : ''} ${daySignaturesMulti.length > 0 ? 'has-signatures' : ''}`}
                        onClick={() => onDayClick && onDayClick(day, daySignaturesMulti.map(item => item.signature))}
                    >
                        <div className="calendar-day-number">{day}</div>
                        {daySignaturesMulti.length > 0 && (
                            <div className="calendar-day-signatures">
                                {daySignaturesMulti.map((item, index) => {
                                    const sig = item.signature;
                                    const group = item.adGroup;
                                    const colors = group.id !== null ? adGroupColors[group.id] : { bg: '#f3f4f6', border: '#9ca3af', text: '#374151' };

                                    return (
                                        <div
                                            key={`${sig.id}-${group.id || 'no-group'}-${index}`}
                                            className={`calendar-signature-item calendar-signature-multi-group ${sig.is_default ? 'is-default' : ''}`}
                                            style={{
                                                backgroundColor: colors.bg,
                                                borderLeftColor: colors.border,
                                                color: colors.text
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSignatureClick && onSignatureClick(sig);
                                            }}
                                            title={`${sig.name} (${group.name})`}
                                        >
                                            <span className="signature-name">{sig.name}</span>
                                            <span className="signature-group-badge">{group.name}</span>
                                            {sig.is_default && <span className="default-star">★</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            } else {
                // Mode simple : afficher LA signature qui sera utilisée
                const daySignatures = getSignaturesForDay(day);

                days.push(
                    <div
                        key={day}
                        className={`calendar-day ${today ? 'calendar-day-today' : ''} ${daySignatures.length > 0 ? 'has-signatures' : ''}`}
                        onClick={() => onDayClick && onDayClick(day, daySignatures)}
                    >
                        <div className="calendar-day-number">{day}</div>
                        {daySignatures.length > 0 && (
                            <div className="calendar-day-signatures">
                                {daySignatures.map(sig => (
                                    <div
                                        key={sig.id}
                                        className={`calendar-signature-item ${sig.is_default ? 'is-default' : ''} ${sig.is_active ? 'is-active' : 'is-inactive'}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onSignatureClick && onSignatureClick(sig);
                                        }}
                                        title={sig.name}
                                    >
                                        <span className="signature-name">{sig.name}</span>
                                        {sig.is_default && <span className="default-star">★</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            }
        }

        return days;
    };

    return (
        <div className="calendar-container">
            <div className="calendar-header">
                <button className="calendar-nav-btn" onClick={previousMonth} title="Mois précédent">
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
                <h2 className="calendar-month-title">{getMonthName(currentDate)}</h2>
                <button className="calendar-nav-btn" onClick={nextMonth} title="Mois suivant">
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
                <button className="calendar-today-btn" onClick={goToToday}>
                    Aujourd'hui
                </button>
            </div>

            <div className="calendar-weekdays">
                <div className="calendar-weekday">Lun</div>
                <div className="calendar-weekday">Mar</div>
                <div className="calendar-weekday">Mer</div>
                <div className="calendar-weekday">Jeu</div>
                <div className="calendar-weekday">Ven</div>
                <div className="calendar-weekday">Sam</div>
                <div className="calendar-weekday">Dim</div>
            </div>

            <div className="calendar-days">
                {renderCalendarDays()}
            </div>
        </div>
    );
}

export default Calendar;
